import Button from "@/common/components/Button"
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters"
import Table from "@/common/components/Table"
import TextField from "@/common/components/TextField"
import { useEstablishmentRoles } from "@/common/hooks/queries/useEstablishmentRoles";
import { useEstablishmentUsers } from "@/common/hooks/queries/useEstablishmentUsers"
import useDebounce from "@/common/hooks/useDebounce"
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { EstablishmentUsersTransformer } from "@/common/services/establishmentUsersTransformer"
import { Mapper } from "@/common/services/mapper"
import { EstablishmentUserData, EstablishmentUsersTableItem } from "@/common/types/establishmentUsers.type"
import { RoleData } from "@/common/types/roles";

import { defaultColumns } from "@/features/establishments/constants/establishmentUsersColumns.constant"
import { SearchIcon } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { userStatusOptions } from "../../../../privateCompanies/pages/CompanyGovernmentAllocations/Show/constants/userStatusOptions";

const UserList = () => {
  const { id } = useParams()

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const [searchRole, setSearchRole] = useDebounce('');

  const { data: rolesOptions } = useEstablishmentRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  const {
    data: establishmentUsers,
    meta,
    isPending: establishmentUsersIsPending,
    isFetching: establishmentUsersIsFetching,
    error: establishmentUsersError = {} as any,
    refetch: refetchEstablishmentUsers,
  } = useEstablishmentUsers({
    filters: {
      search,
      companyId: id || '',
      page: filterParams.page || 1,
      roleId: filterParams.role?.value,
      status: filterParams.status?.value,
    },
    mapper: Mapper.mapWithTransformer<EstablishmentUserData[], EstablishmentUsersTableItem[]>({
      transformer: EstablishmentUsersTransformer.tableTransformer({
        establishmentId: id || '',
      }),
    }),
  })

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={establishmentUsersError?.status}
      refetchFunction={refetchEstablishmentUsers}
    >
      <div className="flex flex-col gap-6 h-[calc(100vh-192px)]">
        <div className="flex flex-col gap-6">
          <TextField
            name="search_user"
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
            }}
            value={filterParams.search}
          />

          <div className="flex justify-between items-center z-50">
            <ListFilters
              filters={[
                {
                  className: 'w-[160px]',
                  onInputChange: setSearchRole,
                  onChange: (role) => setFilterParams((prev) => ({ ...prev, role })),
                  value: filterParams.role,
                  options: [{ label: 'Todos', value: '' }, ...rolesOptions],
                  placeholder: 'Perfil'
                },
                {
                  className: 'w-[128px]',
                  onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                  value: filterParams.status,
                  options: userStatusOptions,
                  placeholder: 'Status'
                }
              ]}
            />

            <Link
              to={establishmentUsersIsPending ? '' : `/establishments/${id}/users/create`}
              className={establishmentUsersIsPending ? 'cursor-default' : ''}
            >
              <Button
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                data-testid="create_establishment_user"
                disabled={establishmentUsersIsPending}
              >
                Novo usuário
              </Button>
            </Link>
          </div>
        </div>

        <Table
          className="max-h-[calc(100vh-326px)]"
          columns={defaultColumns}
          isLoadingData={establishmentUsersIsFetching}
          page={filterParams.page || 1}
          rows={establishmentUsers}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          rowEmptyValue="--"
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </div>
    </ErrorMessageWrapper>
  )
}

export default UserList
